/**
 * THIS IS A FORK THAT DOESN'T USE JQUERY
 *
 * ORIGINAL:
 * Copyright © 2016 Ben Kamens | MIT license | https://github.com/kamens/jQuery-menu-aim
 *
 * menu-aim was a jQuery plugin for dropdown menus that can differentiate
 * between a user trying hover over a dropdown item vs trying to navigate into
 * a submenu's contents.
 *
 * menu-aim assumes that you have are using a menu with submenus that expand
 * to the menu's right. It will fire events when the user's mouse enters a new
 * dropdown item *and* when that item is being intentionally hovered over.
 *
 * __________________________
 * | Monkeys  >|   Gorilla  |
 * | Gorillas >|   Content  |
 * | Chimps   >|   Here     |
 * |___________|____________|
 *
 * In the above example, "Gorillas" is selected and its submenu content is
 * being shown on the right. Imagine that the user's cursor is hovering over
 * "Gorillas." When they move their mouse into the "Gorilla Content" area, they
 * may briefly hover over "Chimps." This shouldn't close the "Gorilla Content"
 * area.
 *
 * This problem is normally solved using timeouts and delays. menu-aim tries to
 * solve this by detecting the direction of the user's mouse movement. This can
 * make for quicker transitions when navigating up and down the menu. The
 * experience is hopefully similar to amazon.com/'s "Shop by Department"
 * dropdown.
 *
 * Use like so:
 *
 *      menuAim(menuElement, {
 *          activate: () => {},  // fired on row activation
 *          deactivate: () => {}  // fired on row deactivation
 *      });
 *
 *  ...to receive events when a menu's row has been purposefully (de)activated.
 *
 *  menuAim() returns a destroy() function that removes every listener it
 *  registered, including a document-level one — call it when the menu is
 *  torn down before the page unloads.
 *
 * The following options can be passed to menuAim. All functions execute with
 * the relevant row's HTML element as the execution context ('this'):
 *
 *      menuAim(menuElement, {
 *          // Function to call when a row is purposefully activated. Use this
 *          // to show a submenu's content for the activated row.
 *          activate: function() {},
 *
 *          // Function to call when a row is deactivated.
 *          deactivate: function() {},
 *
 *          // Function to call when mouse enters a menu row. Entering a row
 *          // does not mean the row has been activated, as the user may be
 *          // mousing over to a submenu.
 *          enter: function() {},
 *
 *          // Function to call when mouse exits a menu row.
 *          exit: function() {},
 *
 *          // Selector for identifying which elements in the menu are rows
 *          // that can trigger the above events. Defaults to ":scope > li".
 *          rowSelector: ":scope > li",
 *
 *          // You may have some menu rows that aren't submenus and therefore
 *          // shouldn't ever need to "activate." If so, filter submenu rows w/
 *          // this selector. Defaults to "*" (all elements).
 *          submenuSelector: "*",
 *
 *          // Direction the submenu opens relative to the main menu. Can be
 *          // left, right, above, or below. Defaults to "right".
 *          submenuDirection: "right"
 *      });
 *
 */

/**
 * @param {Element} element
 * @param {object} opts
 * @returns {() => void} destroy — removes every listener registered by this call.
 */
function menuAim(element, opts) {
  const menu = element
  let activeRow = null
  const mouseLocs = []
  let lastDelayLoc = null
  let timeoutId = null
  const options = Object.assign({
    rowSelector: ':scope > li',
    submenuSelector: '*',
    submenuDirection: 'right',
    tolerance: 75, // bigger = more forgivey when entering submenu
    enter: () => {},
    exit: () => {},
    activate: () => {},
    deactivate: () => {},
    exitMenu: () => {},
  }, opts)

  const MOUSE_LOCS_TRACKED = 3 // number of past mouse locations to track
  const DELAY = 300 // ms delay when user appears to be entering submenu

  /**
   * Keep track of the last few locations of the mouse.
   */
  const mousemoveDocument = function(e) {
    mouseLocs.push({ x: e.pageX, y: e.pageY })

    if (mouseLocs.length > MOUSE_LOCS_TRACKED) {
      mouseLocs.shift()
    }
  }

  /**
   * Cancel possible row activations when leaving the menu entirely
   */
  const mouseleaveMenu = function() {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    // If exitMenu is supplied and returns true, deactivate the
    // currently active row on menu exit.
    if (options.exitMenu(this)) {
      if (activeRow) {
        options.deactivate(activeRow)
      }

      activeRow = null
    }
  }

  /**
   * Trigger a possible row activation whenever entering a new row.
   */
  const mouseenterRow = function() {
    if (timeoutId) {
      // Cancel any previous activation delays
      clearTimeout(timeoutId)
    }

    options.enter(this)
    possiblyActivate(this)
  }
  const mouseleaveRow = function() {
    options.exit(this)
  }

  /*
   * Immediately activate a row if the user clicks on it.
   */
  const clickRow = function() {
    activate(this)
  }

  /**
   * Activate a menu row.
   */
  const activate = function(row) {
    if (row === activeRow) {
      return
    }

    if (activeRow) {
      options.deactivate(activeRow)
    }

    options.activate(row)
    activeRow = row
  }

  /**
   * Possibly activate a menu row. If mouse movement indicates that we
   * shouldn't activate yet because user may be trying to enter
   * a submenu's content, then delay and check again later.
   */
  const possiblyActivate = function(row) {
    const delay = activationDelay()

    if (delay) {
      timeoutId = setTimeout(function() {
        // The mouse may have moved on by the time this fires, e.g. a fast
        // diagonal move that only briefly passed over `row` on the way into
        // the currently open submenu, then stopped there. Once the mouse
        // stops moving there's no more trend for activationDelay() to catch,
        // so it would otherwise activate `row` from a single stale, frozen
        // mouse location. Requiring `row` to still be hovered catches that.
        if (row.matches(':hover')) {
          possiblyActivate(row)
        }
      }, delay)
    } else {
      activate(row)
    }
  }

  /**
   * Return the amount of time that should be used as a delay before the
   * currently hovered row is activated.
   *
   * Returns 0 if the activation should happen immediately. Otherwise,
   * returns the number of milliseconds that should be delayed before
   * checking again to see if the row should be activated.
   */
  const activationDelay = function() {
    if (!activeRow || !activeRow.matches(options.submenuSelector)) {
      // If there is no other submenu row already active, then
      // go ahead and activate immediately.
      return 0
    }

    const box = menu.getBoundingClientRect()
    const viewOffsetX = window.scrollX - document.documentElement.clientLeft
    const viewOffsetY = window.scrollY - document.documentElement.clientTop
    const upperLeft = {
      x: box.left + viewOffsetX,
      y: box.top + viewOffsetY - options.tolerance,
    }
    const upperRight = {
      x: box.right + viewOffsetX,
      y: upperLeft.y,
    }
    const lowerLeft = {
      x: upperLeft.x,
      y: box.bottom + viewOffsetY + options.tolerance,
    }
    const lowerRight = {
      x: upperRight.x,
      y: lowerLeft.y,
    }
    const loc = mouseLocs[mouseLocs.length - 1]
    const prevLoc = mouseLocs[0] || loc

    if (!loc) {
      return 0
    }

    if (prevLoc.x < lowerLeft.x || prevLoc.x > lowerRight.x ||
      prevLoc.y < upperRight.y || prevLoc.y > lowerRight.y) {
      // If the previous mouse location was outside of the entire
      // menu's bounds, immediately activate.
      return 0
    }

    if (lastDelayLoc &&
      loc.x === lastDelayLoc.x && loc.y === lastDelayLoc.y) {
      // If the mouse hasn't moved since the last time we checked
      // for activation status, immediately activate.
      return 0
    }

    // Detect if the user is moving towards the currently activated
    // submenu.
    //
    // If the mouse is heading relatively clearly towards
    // the submenu's content, we should wait and give the user more
    // time before activating a new row. If the mouse is heading
    // elsewhere, we can immediately activate a new row.
    //
    // We detect this by calculating the slope formed between the
    // current mouse location and the upper/lower right points of
    // the menu. We do the same for the previous mouse location.
    // If the current mouse location's slopes are
    // increasing/decreasing appropriately compared to the
    // previous's, we know the user is moving toward the submenu.
    //
    // Note that since the y-axis increases as the cursor moves
    // down the screen, we are looking for the slope between the
    // cursor and the upper right corner to decrease over time, not
    // increase (somewhat counterintuitively).
    function slope(a, b) {
      return (b.y - a.y) / (b.x - a.x)
    }

    let decreasingCorner = upperRight
    let increasingCorner = lowerRight

    // Our expectations for decreasing or increasing slope values
    // depends on which direction the submenu opens relative to the
    // main menu. By default, if the menu opens on the right, we
    // expect the slope between the cursor and the upper right
    // corner to decrease over time, as explained above. If the
    // submenu opens in a different direction, we change our slope
    // expectations.
    if (options.submenuDirection === 'left') {
      decreasingCorner = lowerLeft
      increasingCorner = upperLeft
    } else if (options.submenuDirection === 'below') {
      decreasingCorner = lowerRight
      increasingCorner = lowerLeft
    } else if (options.submenuDirection === 'above') {
      decreasingCorner = upperLeft
      increasingCorner = upperRight
    }

    const decreasingSlope = slope(loc, decreasingCorner)
    const increasingSlope = slope(loc, increasingCorner)
    const prevDecreasingSlope = slope(prevLoc, decreasingCorner)
    const prevIncreasingSlope = slope(prevLoc, increasingCorner)

    if (decreasingSlope < prevDecreasingSlope &&
      increasingSlope > prevIncreasingSlope) {
      // Mouse is moving from previous location towards the
      // currently activated submenu. Delay before activating a
      // new menu row, because user may be moving into submenu.
      lastDelayLoc = loc
      return DELAY
    }

    lastDelayLoc = null
    return 0
  }

  /**
   * Hook up initial menu events
   */

  menu.addEventListener('mouseleave', mouseleaveMenu)

  const rows = menu.querySelectorAll(options.rowSelector)
  for (let i = 0; i < rows.length; ++i) {
    rows[i].addEventListener('mouseenter', mouseenterRow)
    rows[i].addEventListener('mouseleave', mouseleaveRow)
    rows[i].addEventListener('click', clickRow)
  }

  document.addEventListener('mousemove', mousemoveDocument)

  /**
   * Tears down every listener registered above, including the permanent
   * document-level mousemove listener. Callers that may be destroyed before
   * the page unloads (e.g. a component, as opposed to a persistent layout
   * fragment) must call this to avoid leaking one document listener per
   * wired-up menu.
   */
  return function destroy() {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    menu.removeEventListener('mouseleave', mouseleaveMenu)

    for (let i = 0; i < rows.length; ++i) {
      rows[i].removeEventListener('mouseenter', mouseenterRow)
      rows[i].removeEventListener('mouseleave', mouseleaveRow)
      rows[i].removeEventListener('click', clickRow)
    }

    document.removeEventListener('mousemove', mousemoveDocument)
  }
}

export default menuAim
